/**
 * @param {Document} startDoc
 */
export const getRoot = (startDoc) => {
	let doc = startDoc;
	let frame = getFrameElement(doc);
	while (frame) {
		doc = frame.ownerDocument;
		frame = getFrameElement(doc);
	}
	return doc;
};

let rootDocument;

export const getRootDocument = () => (rootDocument || (rootDocument = getRoot(document)));

/**
 * Returns the embedding frame element, if any.
 * @param {Document} doc
 */
export const getFrameElement = (doc) => {
	try {
		return doc.defaultView && doc.defaultView.frameElement || null;
	}
	catch {
		// Ignore the error.
		return null;
	}
};

/**
 * Checks to see if a parent element contains a child element (including inside
 * shadow DOM).
 * @param {Node} parent The parent element.
 * @param {Node} child The child element.
 * @return {boolean} True if the parent node contains the child node.
 */
export const containsDeep = (parent, child) => {
	let node = child;
	while (node) {
		if (node === parent) {
			return true;
		}
		node = getParentNode(node);
	}
	return false;
};

/**
 * Gets the parent node of an element or its host element if the parent node
 * is a shadow root.
 * @param {Node} node The node whose parent to get.
 * @return {Node|null} The parent node or null if no parent exists.
 */
export const getParentNode = (node) => {

	if (node.nodeType === 9 /* Node.DOCUMENT_NODE */ && node !== getRootDocument()) {
		// If this node is a document node, look for the embedding frame.
		// @ts-ignore
		return getFrameElement(node);
	}

	let parent = node.parentNode;

	// If the parent has element that is assigned through shadow root slot
	// @ts-ignore
	if (parent && parent.assignedSlot) {
		// @ts-ignore
		parent = parent.assignedSlot.parentNode;
	}

	// @ts-ignore
	if (parent && parent.nodeType === 11 /* Node.DOCUMENT_FRAGMENT_NODE */ && parent.host) {
		// If the parent is a shadow root, return the host element.
		// @ts-ignore
		return parent.host;
	}

	return parent;
};

/**
 * Returns true if `node` is a Document.
 * @param {!Node} node
 * @returns {boolean}
 */
export const isDoc = node => node && node.nodeType === Node.DOCUMENT_NODE;

/**
 * @param {string|number} value
 */
export const getCssSizeValue = (value) => {
	if (value) {
		// eslint-disable-next-line eqeqeq
		if (Number(value) == value) {
			return value + 'px';
		}
		// Return value as is
		return value;
	}
	return null;
};

/**
 * @param {string} name
 * @param {string} defaultValue
 */
export function getCssVar(name, defaultValue = '') {
	return String(window.getComputedStyle(document.documentElement).getPropertyValue((name.startsWith('--') ? '' : '--') + name) ?? defaultValue).trim();
}

/**
 * @param {*} obj
 */
export const isElement = (obj) => {
	if (!obj || typeof obj !== 'object') {
		return false;
	}

	if (typeof obj.jquery !== 'undefined') {
		obj = obj[0];
	}

	return typeof obj.nodeType !== 'undefined';
};

/**
 * @param {*} obj
 * @returns {HTMLElement?}
 */
export const getElement = (obj) => {
	if (isElement(obj)) { // it's a jQuery object or a node element
		return obj.jquery ? obj[0] : obj;
	}

	if (typeof obj === 'string' && obj.length > 0) {
		return document.querySelector(obj);
	}

	return null;
};

/** @type {string} */
let focusableSelector;

export const getFocusableSelector = () => {
	return focusableSelector || (focusableSelector = [
		'a',
		'button',
		'input',
		'textarea',
		'select',
		'details',
		'[tabindex]',
		'[contenteditable="true"]',
	].map(selector => `${selector}:not([tabindex^="-"])`).join(', '));
};

/**
 * @param {HTMLElement} element
 */
export const isFocusable = (element) => {
	return element.matches(getFocusableSelector()) && !isElementDisabled(element) && isElementDisplayed(element);
};

/**
 * @param {HTMLElement} element
 * @param {string=} filterSelector
 * @returns {HTMLElement[]}
 */
export const getFocusableChildren = (element, filterSelector) => {

	/** @type {HTMLElement[]} */ // @ts-ignore
	const collection = [...element.querySelectorAll(getFocusableSelector())];

	return collection.filter((el) => {
		if (filterSelector && !el.matches(filterSelector)) {
			return false;
		}
		return !isElementDisabled(el) && isElementDisplayed(el);
	});
};

/**
 * @param {HTMLElement} container
 */
export const containerHasFocusedChild = (container) => {
	const { activeElement } = document;
	if (activeElement && activeElement.nodeType === Node.ELEMENT_NODE) {
		return container.contains(activeElement);
	}
	return false;
};

const scrollableOverflowValues = new Set(['auto', 'scroll']);

/**
 * @param {(HTMLElement | Document | Window)=} element
 *
 * @returns {HTMLElement}
 */
export const getScrollParent = (element) => {

	if (!element || element === document || element === self || element === document.scrollingElement) {
		// @ts-ignore
		return document.scrollingElement || document.documentElement;
	}

	/** @type {HTMLElement} */ // @ts-ignore
	const parent = element.parentElement;

	// @ts-ignore
	return parent && (parent.scrollHeight >= parent.clientHeight) && scrollableOverflowValues.has(window.getComputedStyle(parent).overflowY)
		? parent
		: getScrollParent(parent);
};

/**
 * @param {Element | HTMLElement | HTMLFormElement} element
 */
export const isElementDisabled = (element) => {
	if (!element || element.nodeType !== Node.ELEMENT_NODE) {
		return true;
	}

	if (element.classList.contains('disabled')) {
		return true;
	}

	// @ts-ignore: Property 'disabled' does not exist on type 'Element'
	if (typeof element.disabled !== 'undefined') {
		// @ts-ignore: Property 'disabled' does not exist on type 'Element'
		return element.disabled;
	}

	return element.hasAttribute('disabled') && element.getAttribute('disabled') !== 'false';
};

/**
 * @param {Element | HTMLElement} elem
 */
export const isElementDisplayed = elem => Boolean(elem['offsetWidth'] || elem['offsetHeight'] || elem.getClientRects().length > 0);

/**
 * @param {Element | HTMLElement} elem
 */
export const isElementVisible = elem => (isElementDisplayed(elem) && (window.getComputedStyle(elem).getPropertyValue('visibility') === 'visible'));

/**
 * @param {Element | HTMLElement} elem
 */
// export const isElementDetached = elem => !elem.closest('html');
export const isElementDetached = elem => (elem.isConnected === false);

/**
 * @param {HTMLElement} elem
 * @param {CallableFunction} onDetachCallback
 */
export const onDetachElement = (elem, onDetachCallback) => {
	const observer = new MutationObserver(() => {
		if (isElementDetached(elem)) {
			observer.disconnect();
			onDetachCallback();
		}
	});

	observer.observe(document, {
		childList: true,
		subtree: true,
	});
};

/**
 * @param {HTMLElement | Document} element
 * @param {string} eventName
 * @param {CustomEventInit} eventOptions
 */
export const triggerEvent = (element, eventName, eventOptions = {}) => {
	const evt = new CustomEvent(eventName, {
		bubbles: false,
		cancelable: true,
		...eventOptions,
	});
	element.dispatchEvent(evt);
	return evt;
};

/**
 * @link https://stackoverflow.com/questions/2661818/javascript-get-xpath-of-a-node
 * @param {Element} element
 */
export const getXPathForElement = (element) => {
	const idx = (sib, name) => sib
		? idx(sib.previousElementSibling, name || sib.localName) + (sib.localName == name)
		: 1;
	const segs = elm => (!elm || elm.nodeType !== 1
		? ['']
		: (elm.id && document.getElementById(elm.id) === elm
			? [`id("${elm.id}")`]
			: [...segs(elm.parentNode), elm instanceof HTMLElement
				? `${elm.localName}[${idx(elm)}]`
				: `*[local-name() = "${elm.localName}"][${idx(elm)}]`]));
	return segs(element).join('/');
};

/**
 * @param {HTMLTemplateElement} templateElement
 */
export const getTemplateFragment = (templateElement) => {

	if (!templateElement.content) {
		return void 0;
	}

	return templateElement.content.cloneNode(true);
};